import { usePdfExport } from '../hooks/usePdfExport'

/**
 * Persistent, navigation-safe PDF export status toasts.
 * Renders one card per active job, stacked bottom-right.
 * Mounts at app root so it survives route changes.
 */
export default function PdfToast() {
  const { jobs, dismiss } = usePdfExport()

  if (!jobs.length) return null

  return (
    <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 items-end pointer-events-none">
      {jobs.map((job) => (
        <PdfToastCard key={job.jobId} job={job} onDismiss={() => dismiss(job.jobId)} />
      ))}
    </div>
  )
}

function PdfToastCard({ job, onDismiss }) {
  const isActive = ['submitting', 'queued', 'rendering'].includes(job.status)
  const isCompleted = job.status === 'completed'
  const isFailed = job.status === 'failed'

  const title = {
    submitting: 'Submitting PDF request\u2026',
    queued:
      job.position > 1
        ? `Queued \u2014 position ${job.position}`
        : 'Next up \u2014 almost there\u2026',
    rendering: 'Generating your report\u2026',
    completed: 'PDF ready! Downloading\u2026',
    failed: 'PDF generation failed',
  }[job.status]

  const subtitle = {
    submitting: 'Hang on a moment\u2026',
    queued: "You can keep browsing \u2014 we\u2019ll download it when ready.",
    rendering: 'This takes 15\u201330 seconds. Feel free to browse.',
    completed: 'Your report is downloading automatically.',
    failed: job.error || 'Please try again.',
  }[job.status]

  return (
    <div className="w-80 max-w-[calc(100vw-2.5rem)] pointer-events-auto">
      <div
        className={`rounded-xl shadow-2xl border overflow-hidden transition-all ${
          isFailed
            ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
            : isCompleted
            ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
        }`}
      >
        {/* Animated progress bar while active */}
        {isActive && (
          <div className="h-1 bg-slate-100 dark:bg-slate-700 overflow-hidden">
            <div className="h-full bg-primary animate-pulse w-full" />
          </div>
        )}

        <div className="px-4 py-3 flex items-start gap-3">
          {/* Status icon */}
          <div
            className={`mt-0.5 shrink-0 ${
              isFailed
                ? 'text-red-500'
                : isCompleted
                ? 'text-green-600 dark:text-green-400'
                : 'text-primary'
            }`}
          >
            {isFailed ? (
              <span className="material-symbols-outlined text-xl">error</span>
            ) : isCompleted ? (
              <span className="material-symbols-outlined text-xl">check_circle</span>
            ) : (
              <span className="material-symbols-outlined text-xl animate-spin">
                progress_activity
              </span>
            )}
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p
              className={`text-sm font-semibold leading-snug ${
                isFailed
                  ? 'text-red-700 dark:text-red-300'
                  : isCompleted
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-navy-deep dark:text-white'
              }`}
            >
              {title}
            </p>
            <p
              className={`text-xs mt-0.5 leading-relaxed ${
                isFailed
                  ? 'text-red-500 dark:text-red-400'
                  : isCompleted
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {subtitle}
            </p>
          </div>

          {/* Dismiss — only when not actively processing */}
          {(isFailed || isCompleted) && (
            <button
              onClick={onDismiss}
              className="shrink-0 mt-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
              aria-label="Dismiss notification"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
